import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useTipTapDocumentContent } from 'features/tiptap/useTipTapDocumentContent'
import { RichTextValue } from 'features/views/attributes/types'
import { useRecordManagerContext } from 'features/views/RecordManager/useRecordManagerContext'

import { useAttributeContext } from './useAttributeContext'

type UseRichTextAttributeStateProps = {
    isLoading?: boolean
    disableTruncation?: boolean
}

export function useRichTextAttributeState({
    isLoading,
    disableTruncation,
}: UseRichTextAttributeStateProps) {
    const { value, isEditingValue, isEditable, object } = useAttributeContext<RichTextValue>()

    const isEmpty = !isLoading && (!value || (!!value && !value?.plainTextContent))

    const content = useTipTapDocumentContent({
        content: value?.content ?? null,
        format: value?.format ?? 'tiptap',
    })

    const { record } = useRecordManagerContext()

    const [showFullText, setShowFullText] = useState(false)

    const wrapperRef = useRef<HTMLDivElement>(null)

    const editorMaxHeight = useRef(0)
    useLayoutEffect(() => {
        const calculateHeight = requestAnimationFrame(() => {
            if (wrapperRef.current) {
                editorMaxHeight.current = wrapperRef.current.clientHeight
            }
        })

        return () => cancelAnimationFrame(calculateHeight)
    }, [showFullText, value, disableTruncation])

    return useMemo(
        () => ({
            isEmpty,
            content,
            recordSid: record?._sid,
            stackSid: object?.stack_id,
            value,
            isEditingValue,
            isEditable,
            showFullText,
            setShowFullText,
            wrapperRef,
            editorMaxHeight: editorMaxHeight.current,
        }),
        [
            isEmpty,
            content,
            record?._sid,
            object?.stack_id,
            value,
            isEditingValue,
            isEditable,
            showFullText,
            wrapperRef,
        ]
    )
}
